import {
  Box,
  Chip,
  Alert,
  Paper,
  Stack,
  Button,
  MenuItem,
  TextField,
  Typography,
  Autocomplete,
  Grid2 as Grid,
  CircularProgress,
} from '@mui/material';
import type { FeedbackFilterValues } from '../types';
import { FRESHDESK_RATING_VALUES } from '../constants';
import { FormattedMessage, useIntl } from 'react-intl';
import type { FreshdeskResponder } from 'library/types';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { renderAutoCompleteLocaleText, renderDatePickerLocaleText } from 'utils';

type ReportFiltersProps = {
  value: FeedbackFilterValues;
  responders: FreshdeskResponder[];
  activeFilterCount: number;
  hasPendingChanges: boolean;
  loading: boolean;
  respondersLoading: boolean;
  respondersHasError: boolean;
  onChange: (filters: FeedbackFilterValues) => void;
  onApply: () => void;
  onReset: () => void;
  onRetryResponders: () => void;
};

const ReportFilters = ({
  value,
  responders,
  activeFilterCount,
  hasPendingChanges,
  loading,
  respondersLoading,
  respondersHasError,
  onChange,
  onApply,
  onReset,
  onRetryResponders,
}: ReportFiltersProps) => {
  const intl = useIntl();
  const selectedResponder = responders.find((responder) => responder.responderId === value.responderId) || null;
  const setField = <Key extends keyof FeedbackFilterValues>(field: Key, nextValue: FeedbackFilterValues[Key]) => {
    onChange({ ...value, [field]: nextValue });
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <FilterAltOutlinedIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              <FormattedMessage id="freshdeskFeedback.filters.title" />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <FormattedMessage id="freshdeskFeedback.filters.helper" />
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {hasPendingChanges && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={<FormattedMessage id="freshdeskFeedback.filters.pendingChanges" />}
              sx={{ fontWeight: 700 }}
            />
          )}
          {activeFilterCount > 0 && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={intl.formatMessage({ id: 'freshdeskFeedback.filters.activeCount' }, { count: activeFilterCount })}
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>
      </Stack>

      <Grid container spacing={1.5} alignItems="center">
        <Grid size={{ xs: 12, md: 4, xl: 3 }}>
          <Autocomplete
            fullWidth
            options={responders}
            value={selectedResponder}
            loading={respondersLoading}
            {...renderAutoCompleteLocaleText(intl)}
            onChange={(_, responder) => setField('responderId', responder?.responderId ?? null)}
            getOptionLabel={(option) => option.responderName?.trim() || String(option.responderId)}
            isOptionEqualToValue={(option, selected) => option.responderId === selected.responderId}
            slotProps={{
              clearIndicator: { size: 'small' },
              popupIndicator: { size: 'small' },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<FormattedMessage id="freshdeskFeedback.filters.responder" />}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {respondersLoading && <CircularProgress color="inherit" size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 2 }}>
          <DatePicker
            label={<FormattedMessage id="freshdeskFeedback.filters.startDate" />}
            value={value.startDate}
            maxDate={value.endDate || undefined}
            onChange={(date) => setField('startDate', date)}
            localeText={renderDatePickerLocaleText(intl)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 2 }}>
          <DatePicker
            label={<FormattedMessage id="freshdeskFeedback.filters.endDate" />}
            value={value.endDate}
            minDate={value.startDate || undefined}
            onChange={(date) => setField('endDate', date)}
            localeText={renderDatePickerLocaleText(intl)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </Grid>

        <Grid size={{ xs: 6, md: 3, xl: 1.5 }}>
          <TextField
            select
            fullWidth
            label={<FormattedMessage id="freshdeskFeedback.filters.minRate" />}
            value={value.minRate ?? ''}
            onChange={(event) => setField('minRate', event.target.value === '' ? null : Number(event.target.value))}>
            <MenuItem value="">
              <FormattedMessage id="freshdeskFeedback.filters.any" />
            </MenuItem>
            {FRESHDESK_RATING_VALUES.map((rate) => (
              <MenuItem key={rate} value={rate}>
                {rate}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: 6, md: 3, xl: 1.5 }}>
          <TextField
            select
            fullWidth
            label={<FormattedMessage id="freshdeskFeedback.filters.maxRate" />}
            value={value.maxRate ?? ''}
            onChange={(event) => setField('maxRate', event.target.value === '' ? null : Number(event.target.value))}>
            <MenuItem value="">
              <FormattedMessage id="freshdeskFeedback.filters.any" />
            </MenuItem>
            {FRESHDESK_RATING_VALUES.map((rate) => (
              <MenuItem key={rate} value={rate}>
                {rate}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 6, xl: 2 }}>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', xl: 'flex-end' }}>
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              startIcon={<RestartAltRoundedIcon />}
              onClick={onReset}
              disabled={loading}>
              <FormattedMessage id="freshdeskFeedback.filters.reset" />
            </Button>
            <Button
              fullWidth
              variant="contained"
              startIcon={loading ? <CircularProgress color="inherit" size={17} /> : <SearchRoundedIcon />}
              onClick={onApply}
              disabled={loading || !hasPendingChanges}
              sx={(theme) => ({
                '&.Mui-disabled': {
                  color: theme.palette.text.disabled,
                  borderColor: theme.palette.divider,
                  backgroundColor: theme.palette.action.disabledBackground,
                  backgroundImage: 'none',
                  boxShadow: 'none',
                  opacity: 1,
                },
              })}>
              <FormattedMessage id="freshdeskFeedback.filters.apply" />
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {respondersHasError && (
        <Alert
          severity="warning"
          sx={{ mt: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={onRetryResponders} disabled={respondersLoading}>
              <FormattedMessage id="freshdeskFeedback.retry" />
            </Button>
          }>
          <FormattedMessage id="freshdeskFeedback.respondersLoadError" />
        </Alert>
      )}
    </Paper>
  );
};

export default ReportFilters;
